import { useEffect, useMemo } from 'react'
import { useAuthContext } from 'components/auth'
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings'
import { getStatsigEnv } from 'lib/statsig/statsig'
import {
  Loading,
  ModalProvider,
  ToastContainer,
  TooltipProvider,
} from '@workwhile/ui'
import { Bounce } from 'react-toastify'
import V2Pages from 'pages'
import { IntercomProvider } from 'react-use-intercom'
import { track } from 'lib/amplitude'
import { config } from 'config'
import { CompanySwitchDetect } from 'components/CompanySwitchIndicator/CompanySwitchDetect'
import { UpdateBanner } from 'components/UpdateBanner'
import { ThemeProviders } from 'components/ThemeProviders'

function EntryPoint() {
  const onIntercomOpen = () => {
    track('button_click, support')
  }

  return (
    <ThemeProviders googleMapsApiKey={config.googleMapsApiKey}>
      <ToastContainer
        position="top-center"
        style={{ zIndex: 1000000000 }}
        transition={Bounce}
        autoClose={5000}
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
      <TooltipProvider delayDuration={500}>
        <ModalProvider>
          <IntercomProvider
            appId={config.intercomAppId}
            onShow={onIntercomOpen}
          >
            <CompanySwitchDetect />
            <UpdateBanner />
            <V2Pages />
          </IntercomProvider>
        </ModalProvider>
      </TooltipProvider>
    </ThemeProviders>
  )
}

export default function App() {
  const { user, company } = useAuthContext()

  const statsigUser = useMemo(
    () => ({
      userID: user?.uid,
      email: user?.email || undefined,
      customIDs: company?.id
        ? {
            companyId: company?.id.toString(),
          }
        : undefined,
      custom: {
        companyId: company?.id, // to allow statsig to target companyId as a number (lt, gt, etc.)
      },
    }),
    [user?.uid, user?.email, company?.id]
  )

  const { client, isLoading: isLoadingStatsig } = useClientAsyncInit(
    config.statsigApiKey,
    statsigUser,
    {
      environment: { tier: getStatsigEnv() },
    }
  )

  // Update Statsig user when company changes after initial load
  useEffect(() => {
    if (client && company?.id) {
      client.updateUserAsync(statsigUser)
    }
  }, [client, company?.id, statsigUser])

  if (isLoadingStatsig) {
    return <Loading />
  }

  return (
    <StatsigProvider client={client}>
      <EntryPoint />
    </StatsigProvider>
  )
}
